import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { logout } from '../store/authSlice';
import Sidebar from '../components/common/Sidebar';
import Toast from '../components/common/Toast';
import { ResumeEditorModal } from '../components/resume-editor';
import InterviewDetailModal from '../components/interview-builder/InterviewDetailModal';
import { parseMarkdownToLexical } from '../components/resume-editor/utils/generateResumeContent';
import { roundsApi, UpcomingCustomInterview } from '../api/roundsApi';
import {
  customiseResume,
  fetchCustomisedResume,
  fetchResumeCredits,
  fetchAllCustomisedResumes,
  SavedResumeFromDB,
  fetchUserCustomInterviews,
} from '../api/interviewBuilderApi';
import {
  mapCustomInterviewToUpcoming,
  minimalInterviewFromSavedResume,
} from '../utils/interviewDetailUtils';
import { getOrgDepartmentLabels, onboardingApi } from '../api/onboardingApi';
import { vibeService } from '../services/VibeService';
import './BuildResume.css';

const BuildResume: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { user } = useAppSelector((state) => state.auth);

  const interviewId = searchParams.get('interviewId');

  const [activeTab, setActiveTab] = useState<'customise' | 'saved'>('customise');
  const [isLoading, setIsLoading] = useState(false);
  const [isCustomising, setIsCustomising] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string | undefined>();
  const [departmentName, setDepartmentName] = useState<string | undefined>();
  
  const [isResumeEditorOpen, setIsResumeEditorOpen] = useState(false);
  const [resumeEditorContent, setResumeEditorContent] = useState<string | null>(null);
  const [savedResumes, setSavedResumes] = useState<SavedResumeFromDB[]>([]);
  const [viewingResumeId, setViewingResumeId] = useState<string | null>(null);
  const [isLoadingSavedResumes, setIsLoadingSavedResumes] = useState(false);

  // State for listing all interviews when no specific interviewId
  const [allInterviews, setAllInterviews] = useState<UpcomingCustomInterview[]>([]);
  const [customisedInterviewIds, setCustomisedInterviewIds] = useState<Set<string>>(new Set());

  // State for interview detail modal
  const [selectedInterview, setSelectedInterview] = useState<UpcomingCustomInterview | null>(null);
  const [selectedInterviewResume, setSelectedInterviewResume] = useState<{
    exists: boolean;
    resumeMarkdown?: string;
  } | null>(null);
  const [isCheckingResume, setIsCheckingResume] = useState(false);
  /** Opens interview JD modal from Customise tab vs Saved tab (footer differs). */
  const [interviewModalSource, setInterviewModalSource] = useState<'customise' | 'saved' | null>(null);

  // State for resume credits
  const [resumeCredits, setResumeCredits] = useState<number | null>(null);

  // State for toast notification
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Fetch saved resumes from database
  const loadSavedResumes = useCallback(async () => {
    setIsLoadingSavedResumes(true);
    try {
      const { data } = await fetchAllCustomisedResumes();
      setSavedResumes(data.resumes);
    } catch {
      console.error('Failed to fetch saved resumes');
    } finally {
      setIsLoadingSavedResumes(false);
    }
  }, []);

  useEffect(() => {
    loadSavedResumes();
  }, [loadSavedResumes]);

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profileResponse = await onboardingApi.getUserProfile();
        const first = profileResponse.data.first_name || '';
        const last = profileResponse.data.last_name || '';
        setDisplayName(first && last ? `${first} ${last}` : first || last || user?.email?.split('@')[0] || 'User');
        
        const orgDept = getOrgDepartmentLabels(profileResponse.data);
        setOrganizationName(orgDept.organizationName);
        setDepartmentName(orgDept.departmentName);
      } catch {
        setDisplayName(user?.email?.split('@')[0] || 'User');
      }
    };
    fetchProfile();
  }, [user?.email]);

  // Fetch resume credits
  useEffect(() => {
    const loadCredits = async () => {
      try {
        const { data } = await fetchResumeCredits();
        setResumeCredits(data.credits);
      } catch {
        console.error('Failed to fetch resume credits');
      }
    };
    loadCredits();
  }, []);

  // Fetch all interviews when no specific interviewId is selected
  useEffect(() => {
    if (interviewId) {
      // Clear the list when viewing a specific interview
      setAllInterviews([]);
      return;
    }

    const fetchAllInterviews = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data } = await roundsApi.fetchUpcoming();
        setAllInterviews(data.interviews);

        // Check which ones are already customised
        const customisedIds = new Set<string>();
        await Promise.all(
          data.interviews.map(async (interview) => {
            try {
              const { data: resumeData } = await fetchCustomisedResume(interview.id);
              if (resumeData.status === 'completed') {
                customisedIds.add(interview.id);
              }
            } catch {
              // 404 = not customised, ignore
            }
          })
        );
        setCustomisedInterviewIds(customisedIds);
      } catch (err) {
        console.error('Failed to fetch interviews:', err);
        setError('Failed to load interviews');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllInterviews();
  }, [interviewId]);

  const handleLogout = useCallback(() => {
    dispatch(logout());
    navigate('/login');
  }, [dispatch, navigate]);

  const handleOpenInterviewModal = useCallback(async (ci: UpcomingCustomInterview) => {
    setInterviewModalSource('customise');
    setSelectedInterview(ci);
    setSelectedInterviewResume(null);
    setIsCheckingResume(true);

    try {
      const { data: resumeData } = await fetchCustomisedResume(ci.id);
      if (resumeData.status === 'completed' && resumeData.resumeMarkdown) {
        setSelectedInterviewResume({ exists: true, resumeMarkdown: resumeData.resumeMarkdown });
      } else {
        setSelectedInterviewResume({ exists: false });
      }
    } catch {
      setSelectedInterviewResume({ exists: false });
    } finally {
      setIsCheckingResume(false);
    }
  }, []);

  // Deep link (?interviewId=): open same JD modal as customise-tab card, then clean URL for grid behind modal
  useEffect(() => {
    if (!interviewId) return;

    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data } = await roundsApi.fetchUpcoming();
        if (cancelled) return;
        const found = data.interviews.find((i) => i.id === interviewId);
        if (!found) {
          setToastMessage('Interview not found');
          navigate('/build-resume', { replace: true });
          return;
        }
        await handleOpenInterviewModal(found);
        if (cancelled) return;
        navigate('/build-resume', { replace: true });
      } catch {
        if (!cancelled) {
          setToastMessage('Failed to load interview');
          navigate('/build-resume', { replace: true });
        }
      } finally {
        if (cancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [interviewId, navigate, handleOpenInterviewModal]);

  const handleCloseInterviewModal = useCallback(() => {
    setSelectedInterview(null);
    setSelectedInterviewResume(null);
    setInterviewModalSource(null);
  }, []);

  const handleSavedResumeCardOpenModal = useCallback(async (resume: SavedResumeFromDB) => {
    setInterviewModalSource('saved');
    setSelectedInterviewResume({
      exists: true,
      resumeMarkdown: resume.resumeMarkdown,
    });
    setSelectedInterview(minimalInterviewFromSavedResume(resume));
    setIsCheckingResume(true);

    try {
      let interviewLike: UpcomingCustomInterview | null = null;
      const { data: upcoming } = await roundsApi.fetchUpcoming();
      interviewLike = upcoming.interviews.find((i) => i.id === resume.interviewId) ?? null;

      if (!interviewLike) {
        const { data: custom } = await fetchUserCustomInterviews();
        const item = custom.interviews.find((i) => i.id === resume.interviewId);
        if (item) {
          interviewLike = mapCustomInterviewToUpcoming(item, resume.interviewName);
        }
      }

      if (interviewLike) {
        setSelectedInterview(interviewLike);
      }
    } catch {
      /* keep stub from minimalInterviewFromSavedResume */
    } finally {
      setIsCheckingResume(false);
    }
  }, []);

  const handleCustomiseFromModal = useCallback(async () => {
    if (!selectedInterview) return;

    setIsCustomising(true);
    setError(null);

    const POLL_INTERVAL_MS = 2000;
    const MAX_POLL_ATTEMPTS = 60;

    try {
      await customiseResume(selectedInterview.id, 'medium');

      let attempts = 0;
      const pollForResume = async (): Promise<void> => {
        attempts++;
        if (attempts > MAX_POLL_ATTEMPTS) {
          throw new Error('Resume customisation timed out. Please try again.');
        }

        const { data } = await fetchCustomisedResume(selectedInterview.id);

        if (data.status === 'completed' && data.resumeMarkdown) {
          const lexicalContent = parseMarkdownToLexical(data.resumeMarkdown);
          setResumeEditorContent(JSON.stringify(lexicalContent));
          setViewingResumeId(null);
          setIsResumeEditorOpen(true);
          setSelectedInterviewResume({ exists: true, resumeMarkdown: data.resumeMarkdown });
          handleCloseInterviewModal();
          // Add to customised set so card disappears from grid
          setCustomisedInterviewIds((prev) => {
            const newSet = new Set(Array.from(prev));
            newSet.add(selectedInterview.id);
            return newSet;
          });
          // Refresh saved resumes list
          loadSavedResumes();
          // Decrement credits display
          setResumeCredits((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
          return;
        }

        if (data.status === 'failed') {
          throw new Error('Resume customisation failed. Please try again.');
        }

        if (data.status === 'queued' || data.status === 'processing') {
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
          return pollForResume();
        }

        throw new Error('Unexpected status received');
      };

      await pollForResume();
    } catch (err: unknown) {
      console.error('Failed to customise resume:', err);
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 403) {
        setToastMessage('Not enough customisation tokens, earn by taking mock interviews');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to customise resume. Please try again.');
      }
    } finally {
      setIsCustomising(false);
    }
  }, [selectedInterview, handleCloseInterviewModal]);

  const handleStartInterview = useCallback(() => {
    vibeService.show();
  }, []);

  const handleSaveResume = useCallback((content: string) => {
    setResumeEditorContent(content);
  }, []);

  const handleCloseEditor = useCallback(() => {
    setIsResumeEditorOpen(false);
    setResumeEditorContent(null);
    setViewingResumeId(null);
  }, []);

  const handleViewSavedResume = useCallback((resume: SavedResumeFromDB) => {
    setViewingResumeId(resume.id);
    const lexicalContent = parseMarkdownToLexical(resume.resumeMarkdown);
    setResumeEditorContent(JSON.stringify(lexicalContent));
    setIsResumeEditorOpen(true);
  }, []);

  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const fullName = displayName;

  const renderSavedResumeCard = (resume: SavedResumeFromDB) => {
    const skills = resume.skills || [];
    const skillsPreview = skills.slice(0, 4);
    const hasMore = skills.length > 4;

    return (
      <div
        key={resume.id}
        className="saved-resume-card saved-resume-card--interactive"
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('.saved-resume-view-btn')) return;
          handleSavedResumeCardOpenModal(resume);
        }}
      >
        <div className="saved-resume-header">
          <h3 className="saved-resume-name">{resume.interviewName}</h3>
          <span className="saved-resume-date">
            {new Date(resume.createdAt).toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        </div>
        {resume.roleName && (
          <p className="saved-resume-role">{resume.roleName}</p>
        )}
        <div className="saved-resume-skills">
          {skillsPreview.map((skill, i) => (
            <span key={i} className="saved-resume-skill">
              {skill}
            </span>
          ))}
          {hasMore && <span className="saved-resume-skill more">+{skills.length - 4}</span>}
        </div>
        <button
          type="button"
          className="saved-resume-view-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleViewSavedResume(resume);
          }}
        >
          View Resume
        </button>
      </div>
    );
  };

  return (
    <div className="build-resume-page">
      <div style={{ position: 'fixed', left: 0, top: 0, height: '100vh', zIndex: 100 }}>
        <Sidebar
          activeNav="overview"
          onSelectNav={(key) => {
            if (key === 'overview') navigate('/candidate');
            else if (key === 'skills') navigate('/candidate', { state: { activeNav: 'skills' } });
            else if (key === 'activity') navigate('/candidate', { state: { activeNav: 'activity' } });
            else if (key === 'interviews') navigate('/candidate', { state: { activeNav: 'interviews' } });
          }}
          initials={initials}
          name={fullName}
          email={user?.email}
          organizationName={organizationName}
          departmentName={departmentName}
          onStartInterview={handleStartInterview}
          onLogout={handleLogout}
        />
      </div>

      <div className="build-resume-content" style={{ marginLeft: '260px' }}>
        <div className="welcome-section">
          <div className="welcome-left">
            <h1>Build Resume</h1>
            <p className="welcome-message">Customise your resume for specific job descriptions</p>
          </div>
          <div className="welcome-right">
            <div className="credits-display">
              <span className="credits-label">Customisation tokens:</span>
              <span className="credits-value">{resumeCredits ?? '...'}</span>
              <div className="credits-tooltip">
                <svg className="credits-info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <div className="credits-tooltip-content">
                  Complete mock interviews by practicing or taking published interviews to earn customisation tokens
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="activity-tabs">
          <button
            className={`activity-tab-btn ${activeTab === 'customise' ? 'active' : ''}`}
            onClick={() => setActiveTab('customise')}
          >
            Customise
          </button>
          <button
            className={`activity-tab-btn ${activeTab === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveTab('saved')}
          >
            Saved resumes
          </button>
        </div>

        <p className="build-resume-tab-hint">
          {activeTab === 'customise'
            ? 'Customise your resume according to job roles from the active published interviews'
            : 'Showing customised resumes according to job roles from published interviews'}
        </p>

        {activeTab === 'customise' && (
          <div className="build-resume-customise-tab">
            {isLoading ? (
              <div className="build-resume-loading">
                <div className="loading-spinner"></div>
                <p>Loading interview details...</p>
              </div>
            ) : error ? (
              <div className="build-resume-error">
                <p>{error}</p>
              </div>
            ) : (
              (() => {
                const uncustomisedInterviews = allInterviews.filter(
                  (i) => !customisedInterviewIds.has(i.id)
                );
                
                if (uncustomisedInterviews.length > 0) {
                  return (
                    <div className="build-resume-interviews-grid">
                      {uncustomisedInterviews.map((ci) => {
                        const jd = ci.jdExtractedData;
                        const skillsPreview = jd?.skills?.slice(0, 3) || [];
                        const hasMoreSkills = (jd?.skills?.length || 0) > 3;
                        
                        return (
                          <div
                            key={ci.id}
                            className="build-resume-interview-card"
                            onClick={() => handleOpenInterviewModal(ci)}
                          >
                            <div className="interview-card-header">
                              <h3 className="interview-card-name">{ci.name}</h3>
                              <span className="interview-card-deadline">
                                Due: {new Date(ci.deadline).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </span>
                            </div>
                            {jd?.role_name && (
                              <p className="interview-card-role">{jd.role_name}</p>
                            )}
                            {skillsPreview.length > 0 && (
                              <div className="interview-card-skills">
                                {skillsPreview.map((skill, i) => (
                                  <span key={i} className="interview-card-skill">{skill}</span>
                                ))}
                                {hasMoreSkills && (
                                  <span className="interview-card-skill more">
                                    +{(jd?.skills?.length || 0) - 3}
                                  </span>
                                )}
                              </div>
                            )}
                            <button className="interview-card-btn">
                              Customise Resume
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                
                return (
                  <div className="build-resume-empty">
                    <div className="empty-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                      </svg>
                    </div>
                    <h3>No interviews for resume customisation</h3>
                    <p>
                      All your resumes have been customised according to assigned interviews, or you have no new assigned interviews.
                    </p>
                  </div>
                );
              })()
            )}
          </div>
        )}

        {activeTab === 'saved' && (
          <div className="build-resume-saved-tab">
            {isLoadingSavedResumes ? (
              <div className="build-resume-loading">
                <div className="loading-spinner"></div>
                <p>Loading saved resumes...</p>
              </div>
            ) : savedResumes.length === 0 ? (
              <div className="build-resume-empty">
                <div className="empty-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                  </svg>
                </div>
                <h3>No saved resumes</h3>
                <p>Customise a resume from an interview to see it here.</p>
              </div>
            ) : (
              <div className="saved-resumes-grid">
                {savedResumes.map(renderSavedResumeCard)}
              </div>
            )}
          </div>
        )}
      </div>

      <ResumeEditorModal
        isOpen={isResumeEditorOpen}
        onClose={handleCloseEditor}
        initialContent={resumeEditorContent || undefined}
        onSave={handleSaveResume}
      />

      <InterviewDetailModal
        interview={selectedInterview}
        onClose={handleCloseInterviewModal}
        isCheckingResume={isCheckingResume}
        resumeExists={selectedInterviewResume?.exists ?? false}
        resumeMarkdown={selectedInterviewResume?.resumeMarkdown}
        modalSource={interviewModalSource ?? 'customise'}
        canCustomise
        isCustomising={isCustomising}
        onCustomise={handleCustomiseFromModal}
        onViewResume={(markdown) => {
          const lexicalContent = parseMarkdownToLexical(markdown);
          setResumeEditorContent(JSON.stringify(lexicalContent));
          setViewingResumeId(null);
          setIsResumeEditorOpen(true);
          handleCloseInterviewModal();
        }}
      />

      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage(null)}
        />
      )}
    </div>
  );
};

export default BuildResume;
